import { UtilService } from './../../../services/util.service';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { AlertsService } from './../../../services/alerts.service';
import { Component, DestroyRef, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { A11yModule } from "@angular/cdk/a11y";
import { CommonModule } from '@angular/common';
import { MyBrandAndCompetitors } from '../../../services/dashboard.service';
import { StateService } from '../../../services/state.service';
import { FormMode } from '../../../shared/types/common';
import { BrandSelectionTableComponent } from '../brand-selection-table/brand-selection-table.component';
import { AlertFrequency, AlertFrequencyMap, AlertMetric, AlertMetricMap, AlertStepsMap, AlertSubscriptionPayload, AlertSubscriptionResponse, AlertType, EmptyAlertSubscriptionResponse } from '../types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const TOTAL_STEPS = 4;

@Component({
  selector: 'app-alert-form',
  imports: [
    ReactiveFormsModule,
    A11yModule,
    BrandSelectionTableComponent,
    CommonModule,
    AssetVersionPipe
  ],
  templateUrl: './alert-form.component.html',
  styleUrl: './alert-form.component.scss'
})
export class AlertFormComponent {
  @Input() title?: string = undefined;
  @Input() description?: string = undefined;
  @Input() myBrandsAndCompetitors: MyBrandAndCompetitors[] = [];
  @Input() alert?: AlertSubscriptionResponse | EmptyAlertSubscriptionResponse;
  @Output() exit = new EventEmitter<boolean>();

  private alertsService = inject(AlertsService);
  private stateService = inject(StateService);
  private utilService = inject(UtilService);
  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);

  protected currentStep = signal<number>(1);
  protected totalSteps: number = TOTAL_STEPS;

  protected formMode: FormMode = FormMode.ADD;
  protected alertFrequencyMap = AlertFrequencyMap;
  protected alertMetricMap = AlertMetricMap;
  protected alertStepsMap = AlertStepsMap;

  protected frequencyKeys = Object.keys(this.alertFrequencyMap) as AlertFrequency[];
  protected metricKeys = Object.keys(this.alertMetricMap) as AlertMetric[];

  protected form: FormGroup = new FormGroup({
    type: new FormControl<AlertType | null>(null),
    metric: new FormControl<AlertMetric | null>(null),
    frequency: new FormControl<AlertFrequency | null>(null),
    brands: new FormControl<string[]>([]),
  });

  ngOnInit() {
    this.form = this.formBuilder.group({
      type: new FormControl<AlertType | null>(this.alert ? this.alert.alertType : null, Validators.required),
      metric: new FormControl<AlertMetric | null>(this.alert ? this.alert.metric || null : null, Validators.required),
      frequency: new FormControl<AlertFrequency | null>(this.alert ? this.alert.frequency || null : null, Validators.required),
      brands: new FormControl<string[]>(this.alert ? this.alert.brands || [] : [], Validators.required),
    });

    if (this.alert?.subscriptionId) {
      this.formMode = FormMode.EDIT;
    }
  }

  get type(): AlertType | null {
    return this.form.value.type;
  }

  get metric(): AlertMetric | null {
    return this.form.value.metric;
  }

  get frequency(): AlertFrequency | null {
    return this.form.value.frequency;
  }

  get brands(): string[] {
    return this.form.value.brands;
  }

  get canProceedToNextStep(): boolean {
    switch (this.currentStep()) {
      case 1:
        return this.frequency !== null;
      case 2:
        return this.brands.length > 0;
      case 3:
        return this.metric !== null;
      case 4:
        return this.form.valid;
      default:
        return false;
    }
  }

  protected goToNextStep(): void {
    if (this.canProceedToNextStep && this.currentStep() < this.totalSteps) {
      this.currentStep.set(this.currentStep() + 1);
    }
  }

  protected goToPreviousStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.set(this.currentStep() - 1);
    } else {
      this.currentStep.set(1);
      this.exit.emit(false);
    }
  }

  protected submit(): void {
    const payload: AlertSubscriptionPayload = {
      subscriptionId: this.alert?.subscriptionId,
      accountId: this.stateService.activeAccount(),
      alertType: this.form.value.type,
      metric: this.form.value.metric,
      brands: this.form.value.brands,
      frequency: this.form.value.frequency,
      enabled: true
    };

    const method = this.formMode === FormMode.ADD ?
      this.alertsService.createAlertSubscription(payload)
      : this.alertsService.updateAlertSubscription(payload);

    method
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.exit.emit(true);
        },
        error: (error) => {
          this.utilService.openSnackBar('Error occurred. Please try saving again.');
          console.error('Error creating/updating alert subscription:', error);
        }
      });
  }

  protected selectFrequency(frequency: AlertFrequency): void {
    // Don't allow selecting weekly (or any other disabled options)
    if (frequency === AlertFrequency.WEEKLY) {
      return;
    }
    this.form.patchValue({ frequency });
  }

  protected selectMetric(metric: AlertMetric): void {
    this.form.patchValue({ metric });
  }

  protected selectBrand(selectedBrands: string[]): void {
    this.form.patchValue({ brands: selectedBrands });
  }

  protected getMyBrandsForReview(): string[] {
    const result: string[] = [];

    this.myBrandsAndCompetitors.forEach(brand => {
      const myBrand = brand.myBrand;
      if (this.brands.includes(myBrand.key)) {
        result.push(myBrand.label);
      }
    });

    return result;
  }

  protected getCompetitorsForReview(): string[] {
    const result: string[] = [];

    this.myBrandsAndCompetitors.forEach(brand => {
      const competitors = brand.myCompetitors;
      competitors.forEach(competitor => {
        if (this.brands.includes(competitor.key)) {
          result.push(competitor.label);
        }
      });
    });

    return result;
  }

}

